import { useMutation, useQuery, useQueryClient, useInfiniteQuery, QueryClient } from '@tanstack/react-query';
import type {
  ActivityDTO, AppConfigDTO, ContactDTO, ChequeDTO, DepositInfoDTO, InvoiceDTO, MeDTO, SwapQuoteDTO, WalletDTO, WithdrawPreviewDTO, CounterpartyDTO,
} from '@onyx/shared';
import { api, ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api<{ me: MeDTO; startParam: string | null }>('/me'), staleTime: 60_000 });
export const useConfig = () => useQuery({ queryKey: ['config'], queryFn: () => api<AppConfigDTO>('/config'), staleTime: 5 * 60_000 });
export const useWallet = () => useQuery({ queryKey: ['wallet'], queryFn: () => api<WalletDTO>('/wallet'), refetchInterval: 15_000 });

export const useDepositInfo = (asset?: string, network?: string) =>
  useQuery({
    queryKey: ['deposit', asset, network],
    queryFn: () => api<DepositInfoDTO>(`/deposit/${asset}/${network}`),
    enabled: !!asset && !!network,
    staleTime: Infinity,
  });

export type ActivityFilter = { types?: string; asset?: string };

export const useActivity = (filter: ActivityFilter, limit = 30) =>
  useInfiniteQuery({
    queryKey: ['activity', filter, limit],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limit: String(limit) });
      if (filter.types) q.set('types', filter.types);
      if (filter.asset) q.set('asset', filter.asset);
      if (pageParam) q.set('cursor', pageParam);
      return api<{ items: ActivityDTO[]; nextCursor: string | null }>(`/activity?${q}`);
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 15_000,
  });

export const useActivityItem = (id?: string) =>
  useQuery({ queryKey: ['activity-item', id], queryFn: () => api<ActivityDTO>(`/activity/${id}`), enabled: !!id, refetchInterval: 10_000 });

export const useCheques = () => useQuery({ queryKey: ['cheques'], queryFn: () => api<{ items: ChequeDTO[] }>('/cheques') });
export const useInvoices = () => useQuery({ queryKey: ['invoices'], queryFn: () => api<{ items: InvoiceDTO[] }>('/invoices') });
export const useCheque = (id?: string) => useQuery({ queryKey: ['cheque', id], queryFn: () => api<ChequeDTO>(`/cheques/${id}`), enabled: !!id });
export const useInvoice = (id?: string) => useQuery({ queryKey: ['invoice', id], queryFn: () => api<InvoiceDTO>(`/invoices/${id}`), enabled: !!id });

export const useContacts = (q: string) =>
  useQuery({
    queryKey: ['contacts', q],
    queryFn: () => api<{ items: ContactDTO[] }>(`/users/search?q=${encodeURIComponent(q)}`),
    enabled: q === '' || q.replace(/^@/, '').length >= 3,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

export const useLookup = (q: string) =>
  useQuery({
    queryKey: ['lookup', q],
    queryFn: () => api<{ user: CounterpartyDTO | null }>(`/users/lookup?q=${encodeURIComponent(q)}`),
    enabled: q.replace(/^@/, '').length >= 3,
    staleTime: 30_000,
  });

/** After any money movement, everything that shows balances or history is stale. */
export function useMoneyMutation<TVars, TData>(fn: (v: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wallet'] });
      void qc.invalidateQueries({ queryKey: ['activity'] });
      void qc.invalidateQueries({ queryKey: ['cheques'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export const postSwapQuote = (v: { from: string; to: string; amount: string; side: 'from' | 'to' }) => api<SwapQuoteDTO>('/swap/quote', { body: v });
export const postWithdrawPreview = (v: { asset: string; network: string; address: string; amount: string; memo?: string | null }) =>
  api<WithdrawPreviewDTO>('/withdraw/preview', { body: v });
